// Service API pour l'Espace Acheteur
// Gestion des appels API vers le backend

import { API_URL } from '../config/api';

// Helper pour récupérer le token
const getToken = () => {
  return localStorage.getItem('buyer_token');
};

// Helper pour les headers
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Wrapper fetch avec gestion d'erreurs
const apiFetch = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
};

// ============ AUTH ============

export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/api/client/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Identifiants incorrects');
    }
    
    const data = await response.json();
    localStorage.setItem('buyer_token', data.token);
    localStorage.setItem('buyer_user', JSON.stringify(data.user));
    return data;
  },

  logout: async () => {
    try {
      await apiFetch('/api/client/auth/logout', { method: 'POST' });
    } finally {
      localStorage.removeItem('buyer_token');
      localStorage.removeItem('buyer_user');
    }
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('buyer_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!getToken();
  }
};

// ============ FAVORIS ============

export const favoritesAPI = {
  getAll: () => apiFetch('/api/me/favorites'),
  
  add: (product) => apiFetch('/api/me/favorites', {
    method: 'POST',
    body: JSON.stringify({
      product_id: product.id || product.product_id,
      product_name: product.name || product.title || product.product_name,
      product_price: product.price || product.product_price,
      product_image: product.image || product.image_url || product.product_image,
      seller_id: product.seller_id || 'default-seller',
      seller_name: product.seller || product.seller_name || 'Vendeur AbracadaBati'
    })
  }),
  
  remove: (productId) => apiFetch(`/api/me/favorites/${productId}`, {
    method: 'DELETE'
  }),
  
  check: (productId) => apiFetch(`/api/me/favorites/check/${productId}`)
};

// ============ PANIER ============

export const cartAPI = {
  get: () => apiFetch('/api/me/cart'),
  
  addItem: (product, quantity = 1) => apiFetch('/api/me/cart/items', {
    method: 'POST',
    body: JSON.stringify({
      product_id: product.id || product.product_id,
      product_name: product.name || product.title || product.product_name,
      product_price: product.price || product.product_price,
      product_image: product.image || product.image_url || product.product_image,
      seller_id: product.seller_id || 'default-seller',
      seller_name: product.seller || product.seller_name || 'Vendeur AbracadaBati',
      quantity,
      variant: product.variant || null
    })
  }),
  
  updateItem: (itemId, quantity) => apiFetch(`/api/me/cart/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify({ quantity })
  }),
  
  removeItem: (itemId) => apiFetch(`/api/me/cart/items/${itemId}`, {
    method: 'DELETE'
  }),
  
  clear: () => apiFetch('/api/me/cart', { method: 'DELETE' })
};

// ============ ADRESSES ============

export const addressesAPI = {
  getAll: () => apiFetch('/api/me/addresses'),
  
  create: (address) => apiFetch('/api/me/addresses', {
    method: 'POST',
    body: JSON.stringify(address)
  }),
  
  update: (addressId, address) => apiFetch(`/api/me/addresses/${addressId}`, {
    method: 'PUT',
    body: JSON.stringify(address)
  }),
  
  delete: (addressId) => apiFetch(`/api/me/addresses/${addressId}`, {
    method: 'DELETE'
  })
};

// ============ COMMANDES ============

export const ordersAPI = {
  getAll: (status = null, limit = 20, skip = 0) => {
    let url = `/api/me/orders?limit=${limit}&skip=${skip}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (orderId) => apiFetch(`/api/me/orders/${orderId}`),
  
  create: (orderData) => apiFetch('/api/me/orders', {
    method: 'POST',
    body: JSON.stringify(orderData)
  }),
  
  cancel: (orderId) => apiFetch(`/api/me/orders/${orderId}/cancel`, {
    method: 'POST'
  })
};

// ============ HISTORIQUE D'ACHATS ============

export const purchaseHistoryAPI = {
  getAll: (limit = 20) => apiFetch(`/api/me/purchase-history?limit=${limit}`)
};

// ============ STATS ============

export const statsAPI = {
  get: () => apiFetch('/api/me/stats')
};

// ============ VENTES FLASH (public) ============

export const flashSalesAPI = {
  getActive: (limit = 20) => apiFetch(`/api/flash-sales?limit=${limit}`),
  getUpcoming: (limit = 10) => apiFetch(`/api/flash-sales/upcoming?limit=${limit}`),
  getById: (flashSaleId) => apiFetch(`/api/flash-sales/${flashSaleId}`)
};

export default {
  auth: authAPI,
  favorites: favoritesAPI,
  cart: cartAPI,
  addresses: addressesAPI,
  orders: ordersAPI,
  purchaseHistory: purchaseHistoryAPI,
  stats: statsAPI,
  flashSales: flashSalesAPI
};
